import i18n from '@/i18n'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'
import {
  applyChatContactScopeFilter,
  contactMatchesScope,
} from '@/lib/chatContactScope'
import {
  buildContactDuplicateLookupVariants,
  getContactCanonicalKey,
} from '@/utils/contactUtils'

type AccountContact = Database['public']['Tables']['account_contacts']['Row']
type AccountContactInsert = Database['public']['Tables']['account_contacts']['Insert']
type CreateContactInput = Omit<AccountContactInsert, 'id' | 'account_id' | 'created_at' | 'updated_at'>
/** `countryCallingCode` só no lookup de duplicata — não é coluna do INSERT. */
type CreateContactRequest = CreateContactInput & {
  countryCallingCode?: string | null
}

interface UseContactsReturn {
  contacts: AccountContact[]
  setContacts: React.Dispatch<React.SetStateAction<AccountContact[]>>
  loading: boolean
  error: string | null
  setError: React.Dispatch<React.SetStateAction<string | null>>
  createContact: (
    data: CreateContactRequest
  ) => Promise<{ success: boolean; error?: string; contact?: AccountContact }>
  updateContact: (id: string, data: Partial<AccountContact>) => Promise<boolean>
  deleteContact: (id: string) => Promise<boolean>
  refetch: () => Promise<void>
}

export const useContacts = (): UseContactsReturn => {
  const { user } = useAuth()
  const [contacts, setContacts] = useState<AccountContact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchContacts = async (): Promise<void> => {
    if (!user?.account_id || !supabase) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      let listQuery = supabase
        .from('account_contacts')
        .select('*')
        .eq('account_id', user.account_id)

      listQuery = applyChatContactScopeFilter(listQuery, user)

      const { data: contactsData, error: contactsError } = await listQuery
        .order('created_at', { ascending: false })

      if (contactsError) throw contactsError

      setContacts(contactsData || [])
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contatos')
    } finally {
      setLoading(false)
    }
  }

  const refetch = async (): Promise<void> => {
    await fetchContacts()
  }

  const createContact = async (
    data: CreateContactRequest
  ): Promise<{ success: boolean; error?: string; contact?: AccountContact }> => {
    if (!user?.account_id || !supabase) {
      const msg = 'Sessão inválida ou Supabase indisponível.'
      setError(msg)
      return { success: false, error: msg }
    }

    try {
      setError(null)

      const { countryCallingCode, ...insertData } = data
      const jid = insertData.jid ?? ''
      const probe = {
        jid,
        phone: insertData.phone ?? null,
        is_group: Boolean(insertData.is_group) || jid.endsWith('@g.us'),
        countryCallingCode: countryCallingCode ?? null,
      }
      const newKey = getContactCanonicalKey(probe)
      const { phones, jids } = buildContactDuplicateLookupVariants(probe)

      if (phones.length > 0 || jids.length > 0) {
        type DupRow = {
          id: string
          jid: string
          phone: string | null
          is_group: boolean | null
          display_name?: string | null
          name?: string | null
          push_name_last?: string | null
        }
        const merged: DupRow[] = []
        const seen = new Set<string>()
        const phoneVariantSet = new Set(phones)

        if (phones.length > 0) {
          const { data: byPhone, error: e1 } = await supabase
            .from('account_contacts')
            .select('id,jid,phone,is_group,display_name,name,push_name_last')
            .eq('account_id', user.account_id)
            .in('phone', phones)
          if (e1) throw e1
          for (const row of byPhone || []) {
            if (!seen.has(row.id)) {
              seen.add(row.id)
              merged.push(row as DupRow)
            }
          }
        }
        if (jids.length > 0) {
          const { data: byJid, error: e2 } = await supabase
            .from('account_contacts')
            .select('id,jid,phone,is_group,display_name,name,push_name_last')
            .eq('account_id', user.account_id)
            .in('jid', jids)
          if (e2) throw e2
          for (const row of byJid || []) {
            if (!seen.has(row.id)) {
              seen.add(row.id)
              merged.push(row as DupRow)
            }
          }
        }

        const isSameNumber = (r: DupRow): boolean => {
          if (Boolean(r.is_group) !== probe.is_group) return false
          if (getContactCanonicalKey(r) === newKey) return true
          const rPhoneDigits = (r.phone || '').replace(/\D/g, '')
          if (rPhoneDigits && phoneVariantSet.has(rPhoneDigits)) return true
          if (r.jid && jids.includes(r.jid)) return true
          return false
        }

        const conflict = merged.find(isSameNumber)

        console.log('🛂 [useContacts.createContact] Duplicate check:', {
          probe,
          newKey,
          phoneVariants: phones,
          jidVariants: jids,
          candidates: merged.map((r) => ({
            id: r.id,
            jid: r.jid,
            phone: r.phone,
            is_group: r.is_group,
            canonicalKey: getContactCanonicalKey(r),
          })),
          conflictId: conflict?.id ?? null,
        })

        if (conflict) {
          const label =
            conflict.display_name ||
            conflict.name ||
            conflict.push_name_last ||
            ''
          const msg = label
            ? `Já existe um contato com este número de telefone (${label}).`
            : 'Já existe um contato com este número de telefone.'
          setError(msg)
          return { success: false, error: msg }
        }
      }

      // A linha criada volta no próprio insert: quem chama precisa do id na hora, e
      // procurá-la depois na lista em memória lê o estado anterior ao fetchContacts.
      const { data: inserted, error } = await supabase
        .from('account_contacts')
        .insert({ ...insertData, account_id: user.account_id })
        .select()
        .single()

      if (error) throw error

      await fetchContacts()
      return { success: true, contact: inserted as AccountContact }
    } catch (err: any) {
      const msg = err.message || 'Erro ao criar contato'
      setError(msg)
      return { success: false, error: msg }
    }
  }

  const updateContact = async (id: string, data: Partial<AccountContact>): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      const { error } = await supabase
        .from('account_contacts')
        .update(data)
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      await fetchContacts()
      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar contato')
      return false
    }
  }

  const deleteContact = async (id: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)

      // Bloquear exclusão se houver oportunidade no CRM associada ao contato
      const { count: opportunitiesCount, error: oppCheckError } = await supabase
        .from('account_opportunities')
        .select('id', { count: 'exact', head: true })
        .eq('contact_id', id)
        .eq('account_id', user.account_id)

      if (oppCheckError) throw oppCheckError

      if ((opportunitiesCount ?? 0) > 0) {
        setError(i18n.t('crm:errors.deleteBlockedByOpportunities'))
        return false
      }

      const { error } = await supabase
        .from('account_contacts')
        .delete()
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      await fetchContacts()
      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar contato')
      return false
    }
  }

  useEffect(() => {
    if (!user?.account_id || !supabase) {
      return
    }

    console.log('🔄 [useContacts] Setting up contacts subscription')

    fetchContacts()

    const channel = supabase
      .channel('contacts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contacts',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          console.log('📇 [useContacts] Contact change detected:', payload.eventType)

          if (payload.eventType === 'INSERT') {
            const newContact = payload.new as AccountContact
            if (!contactMatchesScope(user, newContact)) {
              console.log('⏭️ [useContacts] New contact out of scope, skipping')
              return
            }
            setContacts(prev => {
              const exists = prev.some(c => c.id === newContact.id)
              if (exists) return prev
              console.log('✅ [useContacts] Adding new contact')
              return [newContact, ...prev]
            })
          } else if (payload.eventType === 'UPDATE') {
            const updatedContact = payload.new as AccountContact
            if (!contactMatchesScope(user, updatedContact)) {
              setContacts(prev => {
                if (!prev.some(c => c.id === updatedContact.id)) return prev
                console.log('🚫 [useContacts] Contact out of scope, removing:', updatedContact.id)
                return prev.filter(c => c.id !== updatedContact.id)
              })
              return
            }
            setContacts(prev => {
              console.log('🔄 [useContacts] Updating contact:', updatedContact.id)
              return prev.map(c => c.id === updatedContact.id ? updatedContact : c)
                .sort((a, b) => {
                  const aTime = a.created_at ? new Date(a.created_at).getTime() : 0
                  const bTime = b.created_at ? new Date(b.created_at).getTime() : 0
                  return bTime - aTime
                })
            })
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as AccountContact).id
            setContacts(prev => {
              console.log('🗑️ [useContacts] Removing contact:', deletedId)
              return prev.filter(c => c.id !== deletedId)
            })
          }
        }
      )
      .subscribe((status) => {
        console.log('🔌 [useContacts] Subscription status:', status)
      })

    return () => {
      console.log('🔌 [useContacts] Unsubscribing from contacts')
      if (supabase) {
        supabase.removeChannel(channel)
      }
    }
  }, [
    user?.account_id,
    user?.auth_user_id,
    user?.view_all_contacts,
    user?.view_unassigned_contacts,
    user?.type,
  ])

  return {
    contacts,
    setContacts,
    loading,
    error,
    setError,
    createContact,
    updateContact,
    deleteContact,
    refetch,
  }
}
